/**
 * Web edit dialog for a non-CV ("portable") skill: every field on one page -
 * icon, name, description, the method (instructions) and the recommended
 * model - then Save. Twin of the native PortableSkillEditor;
 * CV skills keep the stepped CreateSkillDialog.
 */
import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { WebDialog } from '../common/WebDialog.web';
import { NativeButton } from '../common/NativeButton';
import { useToast } from '../../hooks/useToast';
import { useUpgradeModal } from '../../providers/useUpgradeModal';
import { isEntitlementDenied, updateSkill } from '../../lib/skills-api';
import { entitlementMode } from '../../lib/skills-entitlement';
import { ModelPicker, SkillIconPicker } from './skill-builder-fields.web';
import { INSTRUCTIONS_MAX, draftToSkillInput, type SkillBuilderDraft } from './skill-builder-shared';

export function PortableSkillDialog({
  skillId,
  initialDraft,
  onDone,
  onCancel,
}: {
  skillId: string;
  initialDraft: SkillBuilderDraft;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation('common');
  const toast = useToast();
  const upgradeModal = useUpgradeModal();
  const [draft, setDraft] = useState<SkillBuilderDraft>(initialDraft);
  const [submitting, setSubmitting] = useState(false);
  const patch = (partial: Partial<SkillBuilderDraft>) =>
    setDraft((prev) => ({ ...prev, ...partial }));
  const canSave = draft.name.trim().length > 0 && draft.description.trim().length > 0 && !submitting;

  const save = async () => {
    if (!canSave) return;
    setSubmitting(true);
    try {
      await updateSkill(skillId, draftToSkillInput(draft));
      toast.success(t('skills.builder.saved'));
      onDone();
    } catch (error) {
      if (isEntitlementDenied(error)) {
        upgradeModal.open(entitlementMode(error.decision));
        onCancel();
        return;
      }
      toast.error(t('skills.builder.failed'), error instanceof Error ? error.message : undefined);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <WebDialog
      title={t('skills.builder.editTitle')}
      onClose={onCancel}
      maxWidth={680}
      zIndex={9999}
      footer={
        <div style={{ display: 'flex', gap: 10 }}>
          <NativeButton
            title={t('actions.cancel')}
            onPress={onCancel}
            disabled={submitting}
            variant="outline"
            size="lg"
            pill
            style={{ flex: 1 }}
          />
          <NativeButton
            title={t('skills.builder.save')}
            onPress={() => void save()}
            disabled={!canSave}
            loading={submitting}
            color="contrast"
            size="lg"
            pill
            style={{ flex: 1 }}
          />
        </div>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div>
          <div style={label}>{t('skills.builder.basics.nameLabel')}</div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            <SkillIconPicker
              value={draft.iconKey}
              region={draft.region}
              iconUrl={draft.iconUrl}
              onChange={(icon) => patch({ iconKey: icon, iconUrl: '' })}
              onImageChange={(url) => patch({ iconUrl: url })}
            />
            <input
              value={draft.name}
              dir="auto"
              onChange={(e) => patch({ name: e.target.value })}
              placeholder={t('skills.builder.basics.namePlaceholder')}
              style={input}
            />
          </div>
        </div>
        <div>
          <div style={label}>{t('skills.builder.basics.descriptionLabel')}</div>
          <textarea
            value={draft.description}
            dir="auto"
            onChange={(e) => patch({ description: e.target.value })}
            placeholder={t('skills.builder.basics.descriptionPlaceholder')}
            rows={2}
            style={textarea}
          />
        </div>
        <div>
          <div style={label}>{t('skills.builder.type.instructionsLabel')}</div>
          <textarea
            value={draft.instructions}
            dir="auto"
            maxLength={INSTRUCTIONS_MAX}
            onChange={(e) => patch({ instructions: e.target.value })}
            placeholder={t('skills.builder.type.instructionsPlaceholder')}
            rows={8}
            style={textarea}
          />
        </div>
        <div>
          <div style={label}>{t('skills.newFlow.basics.recommendedModel')}</div>
          <ModelPicker
            value={draft.model}
            onChange={(model) => patch({ model })}
            onUpgrade={() => upgradeModal.open('individual')}
          />
        </div>
      </div>
    </WebDialog>
  );
}

const label: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: 'var(--text-secondary)',
  marginBottom: 8,
};

const input: CSSProperties = {
  flex: 1,
  minWidth: 0,
  width: '100%',
  height: 48,
  borderRadius: 9999,
  border: '1px solid var(--input-border)',
  padding: '0 16px',
  fontSize: 15,
  color: 'var(--text-primary)',
  background: 'rgba(0,0,0,0.04)',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const textarea: CSSProperties = {
  width: '100%',
  borderRadius: 16,
  border: '1px solid var(--input-border)',
  padding: '14px 16px',
  fontSize: 15,
  lineHeight: '22px',
  color: 'var(--text-primary)',
  background: 'rgba(0,0,0,0.04)',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
  resize: 'vertical',
};
